import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    MenuItem,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import WorkFileService, { folderLink, workErrorMessage } from '../../services/WorkFileService';
import { formatShortDate, timeAgo } from '../../utils/dates';
import { shortName } from '../tasks/taskUi';

const EXPIRY = [
    { value: 'never', label: 'Never' },
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' },
    { value: 'date', label: 'On a date' },
];

const ACCESS = [
    { value: 'VIEW', label: 'Can view' },
    { value: 'EDIT', label: 'Can add files' },
];

/** What a link lets people do, in one sentence for the dialog. */
export const accessText = (access, what) =>
    access === 'EDIT'
        ? `Anyone with this link can see, play and download ${what}, and add files. They can't delete or replace files.`
        : `Anyone with this link can see, play and download ${what}. They can't add or change anything.`;

const describe = (l) => {
    const parts = [l.label, l.access === 'EDIT' ? 'can add files' : 'can view'];
    if (l.expired) parts.push(`expired ${formatShortDate(l.expiresAt)}`);
    else parts.push(l.expiresAt ? `expires ${formatShortDate(l.expiresAt)}` : 'never expires');
    parts.push(`created by ${shortName(l.createdBy)}`);
    if (l.accessCount > 0) {
        parts.push(`opened ${l.accessCount} time${l.accessCount === 1 ? '' : 's'}, last ${timeAgo(l.lastAccessedAt)}`);
    } else {
        parts.push('not opened yet');
    }
    return parts.join(' · ');
};

/**
 * Public, no-login links to a video's shared folder (PMs and admins). A link reaches the whole
 * video or one language, lets people view or also add files, and lasts for ever or until a date.
 * Opens on the language the page was opened from.
 */
const WorkShareDialog = ({ open, onClose, page }) => {
    useTrackUsage('WorkShareDialog', 'dialog', 'working-folder', open);
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scope, setScope] = useState('');
    const [access, setAccess] = useState('VIEW');
    const [expiry, setExpiry] = useState('never');
    const [date, setDate] = useState('');
    const [error, setError] = useState(null);
    const [snack, setSnack] = useState(null);
    const [confirmOff, setConfirmOff] = useState(null);

    const languages = page.folders.filter((f) => f.languageKey);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            setLinks(await WorkFileService.shareLinks(page.contentType, page.contentId));
        } catch (e) {
            setError(workErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [page.contentType, page.contentId]);

    useEffect(() => {
        if (open) {
            setError(null);
            // A waiting task's folder is there to receive the video
            setAccess(page.waiting ? 'EDIT' : 'VIEW');
            setExpiry('never');
            setDate('');
            setScope(page.openedFromLanguage || '');
            load();
        }
    }, [open, load, page.openedFromLanguage, page.waiting]);

    const scopeLabel = scope ? languages.find((f) => f.languageKey === scope)?.label : null;
    const word = { POST: 'post', AUDIO: 'audio' }[page.contentType] || 'video';
    const what = scopeLabel ? `the ${scopeLabel} folder` : `every folder of this ${word}`;

    const expiresAt = () => {
        if (expiry === 'never') return null;
        if (expiry === 'date') return date ? new Date(`${date}T23:59:59`).toISOString() : null;
        return new Date(Date.now() + Number(expiry) * 86400000).toISOString();
    };

    const copy = async (token) => {
        try {
            await navigator.clipboard.writeText(folderLink(token));
            setSnack('Link copied');
        } catch {
            setSnack(folderLink(token));
        }
    };

    const create = async () => {
        setError(null);
        try {
            const link = await WorkFileService.createShareLink(page.contentType, page.contentId, {
                languageKey: scope || null,
                access,
                expiresAt: expiresAt(),
            });
            await load();
            await copy(link.token);
        } catch (e) {
            setError(workErrorMessage(e));
        }
    };

    const turnOff = async () => {
        const link = confirmOff;
        setConfirmOff(null);
        try {
            await WorkFileService.turnOffShareLink(page.contentType, page.contentId, link.id);
            setSnack('Link turned off');
            await load();
        } catch (e) {
            setError(workErrorMessage(e));
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Share {page.title}</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2">Create a public link</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <TextField select size="small" label="Folder" value={scope} onChange={(e) => setScope(e.target.value)} sx={{ minWidth: 200 }}>
                            <MenuItem value="">Whole {word} (every language)</MenuItem>
                            {languages.map((f) => (
                                <MenuItem key={f.languageKey} value={f.languageKey}>
                                    {f.label} only
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField select size="small" label="Access" value={access} onChange={(e) => setAccess(e.target.value)} sx={{ minWidth: 150 }}>
                            {ACCESS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField select size="small" label="Expires" value={expiry} onChange={(e) => setExpiry(e.target.value)} sx={{ minWidth: 130 }}>
                            {EXPIRY.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {expiry === 'date' && (
                            <TextField
                                size="small"
                                type="date"
                                label="Expiry date"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        {accessText(access, what)} No login needed. They don't see tasks, comments or email addresses.
                    </Typography>
                    {access === 'EDIT' && expiry === 'never' && (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            This link never expires. Anyone it's passed on to can keep adding files until you turn it off.
                        </Alert>
                    )}
                    <Button variant="contained" sx={{ mt: 1.5 }} onClick={create} disabled={expiry === 'date' && !date}>
                        Create and copy link
                    </Button>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2">Links</Typography>
                    {loading && <CircularProgress size={20} sx={{ mt: 1 }} />}
                    {!loading && links.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No public links yet.
                        </Typography>
                    )}
                    {links.map((l) => (
                        <Box key={l.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider', opacity: l.expired ? 0.55 : 1 }}>
                            <Typography variant="body2" noWrap title={folderLink(l.token)} sx={{ fontFamily: 'monospace' }}>
                                {folderLink(l.token).slice(0, 48)}…
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div">
                                {describe(l)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                {!l.expired && (
                                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copy(l.token)}>
                                        Copy
                                    </Button>
                                )}
                                <Button size="small" color="error" onClick={() => setConfirmOff(l)}>
                                    Turn off
                                </Button>
                            </Box>
                        </Box>
                    ))}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Done</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!confirmOff} onClose={() => setConfirmOff(null)} maxWidth="xs">
                <DialogTitle>Turn off this link?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Anyone who opens it will see "This link is no longer available". Uploads still running through it won't be added.
                        This can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOff(null)}>Cancel</Button>
                    <Button color="error" onClick={turnOff}>
                        Turn off
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snack}
                autoHideDuration={4000}
                onClose={() => setSnack(null)}
                message={<span aria-live="polite">{snack}</span>}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            />
        </>
    );
};

export default WorkShareDialog;
